const refreshDropdown   = require('@binary-com/binary-style').selectDropdown;
const moment            = require('moment');
const { localize }      = require('@deriv-com/translations');
const TradingAnalysis   = require('./analysis');
const commonTrading     = require('./common');
const Contract          = require('./contract');
const Defaults          = require('./defaults');
const Durations         = require('./duration');
const GetTicks          = require('./get_ticks');
const Price             = require('./price');
const Reset             = require('./reset');
const Symbols           = require('./symbols');
const Tick              = require('./tick');
const NotAvailable      = require('./not-available.jsx');
const BinarySocket       = require('../../base/socket');
const { mapErrorMessage } = require('../../../_common/error_mapper');
const { triggerMarketChange } = require('../../hooks/events');
const dataManager       = require('../../common/data_manager.js').default;
const isCryptocurrency  = require('../../common/currency').isCryptocurrency;
const elementInnerHtml  = require('../../../_common/common_functions').elementInnerHtml;
const getElementById    = require('../../../_common/common_functions').getElementById;
const getVisibleElement = require('../../../_common/common_functions').getVisibleElement;
const State             = require('../../../_common/storage').State;
const getPropertyValue  = require('../../../_common/utility').getPropertyValue;

const Process = (() => {
    const {
        AMOUNT,
        AMOUNT_TYPE,
        DURATION_AMOUNT,
        DURATION_UNITS,
        CURRENCY,
        END_DATE,
        EXPIRY_DATE,
        EXPIRY_TIME,
        EXPIRY_TYPE,
        FORM_NAME,
        IS_EQUAL,
        MARKET,
        PREDICTION,
        SELECTED_TICK,
        UNDERLYING,
    } = Defaults.PARAM_NAMES;

    /*
     * This function processes the active symbols to get markets
     * and underlying list
     */
    const processActiveSymbols = (country, response = null) => {
        // If response is provided (from InitializationManager), use it directly
        // Otherwise, make the API call (fallback for direct calls)
        const processResponse = (apiResponse) => {

            // Check for API errors first
            if (apiResponse && apiResponse.error) {
                // eslint-disable-next-line no-console
                console.error('Active symbols API error:', apiResponse.error);
                NotAvailable.init({
                    title: localize('API Error'),
                    body : localize(`Error loading market data: ${mapErrorMessage(apiResponse.error) || 'Unknown error'}`),
                });
                return;
            }

            // Removed country/EU restrictions - allow all accounts to access SmartTrader
            if (apiResponse && apiResponse.active_symbols && apiResponse.active_symbols.length) {
                // populate the Symbols object
                Symbols.details(apiResponse);

                const market = commonTrading.getDefaultMarket();

                // store the market
                Defaults.set(MARKET, market);

                // Set default underlying before displaying markets to avoid race condition
                // This ensures the underlying input has a value before React components render asynchronously
                const underlyings = Symbols.underlyings()[market];
                if (underlyings) {
                    const underlying_keys = Object.keys(underlyings);
                    if (underlying_keys.length > 0) {
                        const default_underlying = Defaults.get(UNDERLYING) || underlying_keys[0];
                        Defaults.set(UNDERLYING, default_underlying);
                        const underlying_element = document.getElementById('underlying');
                        if (underlying_element) {
                            underlying_element.value = default_underlying;
                        }
                    }
                }

                commonTrading.displayMarkets();
                processMarket();
                
            } else {
                // Show generic error if no active symbols are available

                NotAvailable.init({
                    title: localize('No trading symbols available'),
                    body : localize('There are currently no trading symbols available. Please try again later.'),
                });
            }
        };

        if (response) {
            // Response provided by InitializationManager
            processResponse(response);
        } else {
            // Fallback: make API call directly (for backward compatibility)
            // Use 'full' instead of 'brief' and add debug logging
            // eslint-disable-next-line no-console
            console.log('Requesting active_symbols for unauthenticated user...');
            
            BinarySocket.send({ active_symbols: 'brief' }).then((apiResponse) => {
                // eslint-disable-next-line no-console
                console.log('Active symbols response:', apiResponse);
                processResponse(apiResponse);
            }).catch((error) => {
                // eslint-disable-next-line no-console
                console.error('Failed to load active symbols:', error);
                // Show error state but don't block the page
                NotAvailable.init({
                    title: localize('Connection Error'),
                    body : localize('Unable to load market data. Please refresh the page.'),
                });
            });
        }
    };

    /*
     * Function to call when market has changed
     */
    const processMarket = () => {
        // we can get market from sessionStorage as allowed market
        // is already set when this is called
        let market = Defaults.get(MARKET);

        // change to default market if query string contains an invalid market
        if (!market || !Symbols.underlyings()[market]) {
            market = commonTrading.getDefaultMarket();
            Defaults.set(MARKET, market);
        }

        processMarketUnderlying();
    };

    /*
     * Function to call when underlying has changed
     */
    const processMarketUnderlying = () => {
        const underlying_element = document.getElementById('underlying');
        const underlying = underlying_element?.value;

        // Early return if no underlying selected (can happen during initialization)
        if (!underlying) {
            // eslint-disable-next-line no-console
            console.warn('processMarketUnderlying: No underlying selected, skipping');
            hideLoading();
            return;
        }

        Defaults.set(UNDERLYING, underlying);

        // Trigger market change event to update React components
        triggerMarketChange();

        commonTrading.showFormOverlay();

        // get ticks for current underlying
        GetTicks.request(underlying);

        Tick.clean();

        commonTrading.updateWarmChart();

        BinarySocket.clearTimeouts();

        getContracts(underlying);

        commonTrading.displayTooltip();
    };

    const getContracts = (underlying) => {
        if (!underlying) {
            // eslint-disable-next-line no-console
            console.error('getContracts: underlying is null/undefined');
            hideLoading();
            return;
        }
        
        BinarySocket.send({ contracts_for: underlying }).then((response) => {
            processContract(response);
        }).catch((error) => {
            // eslint-disable-next-line no-console
            console.error('Failed to load contracts for', underlying, ':', error);
            // Still call hideLoading to ensure page becomes visible
            hideLoading();
        });
    };

    const hideLoading = () => {
        try {
            const container = getElementById('trading_socket_container');
            if (container) {
                container.classList.add('show');
            }
            
            const init_logo = getElementById('trading_init_progress');

            dataManager.setContract({
                hide_page_loader: true,
            });
            
            if (init_logo && init_logo.style.display !== 'none') {
                init_logo.style.display = 'none';
                
                // Synchronize header skeleton loaders with trading completion
                // Wait for header to be ready before updating skeleton loaders
                // Use SmartTrader namespace to avoid circular dependency
                if (typeof window !== 'undefined' && window.SmartTrader?.Header?.addHeaderReadyCallback) {
                    window.SmartTrader.Header.addHeaderReadyCallback(() => {
                        try {
                            if (window.SmartTrader?.Header?.updateLoginButtonsDisplay) {
                                window.SmartTrader.Header.updateLoginButtonsDisplay();
                            }
                        } catch (error) {
                            // console.warn('Header module not available in callback:', error);
                        }
                    });
                } else {
                    // Fallback if callback system not available via SmartTrader namespace
                    try {
                        if (window.SmartTrader?.Header?.updateLoginButtonsDisplay) {
                            window.SmartTrader.Header.updateLoginButtonsDisplay();
                        }
                    } catch (error) {
                        // console.warn('Header module not available for fallback:', error);
                    }
                }
                
                // Additional fallback: directly update header skeleton loaders if Header module not available
                if (typeof window === 'undefined' || !window.SmartTrader?.Header) {
                    const skeleton_login = document.querySelector('.skeleton-btn-login');
                    const skeleton_signup = document.querySelector('.skeleton-btn-signup');
                    const btn_login = document.getElementById('btn__login');
                    const btn_signup = document.getElementById('btn__signup');
                    
                    if (skeleton_login) skeleton_login.remove();
                    if (skeleton_signup) skeleton_signup.remove();
                    
                    // Simple authentication check: if there is a session_token, user is logged in
                    const session_token = localStorage.getItem('session_token');
                    const is_logged_in = !!session_token;
                    
                    if (!is_logged_in) {
                        if (btn_login) btn_login.style.display = 'flex';
                        if (btn_signup) btn_signup.style.display = 'flex';
                    }
                }
                
                Defaults.update();
            }
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Error in hideLoading:', error);
            // Ensure page becomes visible even if there are errors
            const container = getElementById('trading_socket_container');
            if (container) {
                container.classList.add('show');
            }
        }
    };

    const handleNotOfferedSymbol = () => {
        const market = commonTrading.getDefaultMarket();
        Defaults.set(MARKET, market);
        
        const underlyings = Symbols.underlyings()[market] || {};
        const underlying = Object.keys(underlyings)[0];
        Defaults.set(UNDERLYING, underlying);
    };

    /*
     * Function to display contract form for current underlying
     */
    const processContract = (contracts) => {
        if (getPropertyValue(contracts, ['error', 'code']) === 'InvalidSymbol') {
            Price.processForgetProposals();
            dataManager.setPurchase({
                show_purchase_results: true,
            });
            getElementById('contract_confirmation_container').style.display = 'block';
            getElementById('contracts_list').style.display = 'none';
            getElementById('confirmation_message').hide();
            const confirmation_error = getElementById('confirmation_error');
            confirmation_error.setVisibility(1);
            handleNotOfferedSymbol();
            elementInnerHtml(confirmation_error, `${mapErrorMessage(contracts.error)} <a onclick="sessionStorage.removeItem('underlying'); window.location.reload();">${localize('Please reload the page')}</a>`);
            hideLoading();

            return;
        }
        State.set('is_chart_allowed', !(contracts.contracts_for && contracts.contracts_for.feed_license && contracts.contracts_for.feed_license === 'chartonly'));

        hideLoading();

        Contract.setContracts(contracts);

        const contract_categories = Contract.contractForms();
        let formname;
        if (Defaults.get(FORM_NAME) && contract_categories && contract_categories[Defaults.get(FORM_NAME)]) {
            formname = Defaults.get(FORM_NAME);
        } else {
            const tree = commonTrading.getContractCategoryTree(contract_categories);
            if (tree[0]) {
                if (typeof tree[0] === 'object') {
                    formname = tree[0][1][0];
                } else {
                    formname = tree[0];
                }
            }
        }

        commonTrading.displayContractForms('contract_form_name_nav', contract_categories, formname);

        processContractForm(formname);

        TradingAnalysis.request();

        commonTrading.hideFormOverlay();
    };

    const processContractForm = (formname_to_set = getElementById('contract').value || Defaults.get(FORM_NAME)) => {
        setFormName(formname_to_set);

        // get updated formname
        Contract.details(Defaults.get(FORM_NAME));

        displayPrediction();
        refreshDropdown('#prediction');
        displaySelectedTick();
        refreshDropdown('#selected_tick');
        if (!Reset.isReset(Defaults.get(FORM_NAME))) {
            Reset.hideResetTime();
        }

        Durations.display();

        // needs to be called after durations are populated
        displayEquals();

        const currency  = Defaults.get(CURRENCY) || getVisibleElement('currency').value;
        const is_crypto = isCryptocurrency(currency);
        const amount    = is_crypto ? 'amount_crypto' : AMOUNT;
        if (Defaults.get(amount)) {
            $('#amount').val(Defaults.get(amount));
        } else {
            let default_value;
            
            const contracts = Contract.contracts().contracts_for;
            if (contracts && contracts.default_stake) {
                default_value = contracts.default_stake;
            } else {
                default_value = 10;
            }
            
            Defaults.set(amount, default_value);
            getElementById('amount').value = default_value;
        }
        if (Defaults.get(AMOUNT_TYPE)) {
            commonTrading.selectOption(Defaults.get(AMOUNT_TYPE), getElementById('amount_type'));
        } else {
            Defaults.set(AMOUNT_TYPE, getElementById('amount_type').value);
        }

        getElementById('stake_option').setVisibility(1);
        refreshDropdown('#amount_type');

        if (Defaults.get(CURRENCY)) {
            commonTrading.selectOption(Defaults.get(CURRENCY), getVisibleElement('currency'));
        }

        const expiry_type        = Defaults.get(EXPIRY_TYPE) || 'duration';
        const make_price_request = onExpiryTypeChange(expiry_type);

        if (make_price_request >= 0) {
            Price.processPriceRequest();
        }
    };

    const displayPrediction = () => {
        const prediction_row = getElementById('prediction_row');
        if (Contract.form() === 'digits' && sessionStorage.getItem('formname') !== 'evenodd') {
            prediction_row.show();
            const prediction = getElementById('prediction');
            if (Defaults.get(PREDICTION)) {
                commonTrading.selectOption(Defaults.get(PREDICTION), prediction);
            } else {
                Defaults.set(PREDICTION, prediction.value);
            }
        } else {
            prediction_row.hide();
            Defaults.remove(PREDICTION);
        }
    };

    const displaySelectedTick = () => {
        const selected_tick_row       = getElementById('selected_tick_row');
        const highlowticks_expiry_row = getElementById('highlowticks_expiry_row');
        if (sessionStorage.getItem('formname') === 'highlowticks') {
            selected_tick_row.show();
            highlowticks_expiry_row.show();
            const selected_tick = getElementById('selected_tick');
            if (Defaults.get(SELECTED_TICK)) {
                commonTrading.selectOption(Defaults.get(SELECTED_TICK), selected_tick);
            } else {
                Defaults.set(SELECTED_TICK, selected_tick.value);
            }
        } else {
            selected_tick_row.hide();
            highlowticks_expiry_row.hide();
            Defaults.remove(SELECTED_TICK);
        }
    };

    const hasCallPutEqual = (contracts = getPropertyValue(Contract.contracts(), ['contracts_for', 'available']) || []) =>
        contracts.find(contract => contract.contract_category === 'callputequal');

    const displayEquals = (expiry_type = 'duration') => {
        const formname  = Defaults.get(FORM_NAME);
        const el_equals = document.getElementById('callputequal');
        const durations
            = getPropertyValue(Contract.durations(), [commonTrading.durationType(Defaults.get(DURATION_UNITS))]) || [];
        if (/^(callputequal|risefall)$/.test(formname) && (('callput' in durations || 'callputequal' in durations || expiry_type === 'endtime') && hasCallPutEqual())) {
            if (+Defaults.get(IS_EQUAL)) {
                el_equals.checked = true;
            }
            el_equals.parentElement.setVisibility(1);
            dataManager.setTrade({
                show_allow_equals: true,
            });
        } else {
            el_equals.parentElement.setVisibility(0);
            dataManager.setTrade({
                show_allow_equals: false,
            });
        }
    };

    const setFormName = (formname) => {
        let formname_to_set    = formname;
        const has_callputequal = hasCallPutEqual();
        if (formname_to_set === 'callputequal' && (!has_callputequal || !+Defaults.get(IS_EQUAL))) {
            formname_to_set = 'risefall';
        } else if (formname_to_set === 'risefall' && has_callputequal && +Defaults.get(IS_EQUAL)) {
            formname_to_set = 'callputequal';
        }
        Defaults.set(FORM_NAME, formname_to_set);
        getElementById('contract').setAttribute('value', formname_to_set);
    };

    const forgetTradingStreams = () => {
        Price.processForgetProposals();
        Price.processForgetProposalOpenContract();
        processForgetTicks();
    };

    /*
     * cancel the current tick stream
     * this need to be invoked before makin
     */
    const processForgetTicks = () => {
        BinarySocket.send({ forget_all: 'ticks' });
    };

    const onExpiryTypeChange = (value) => {
        const $expiry_type    = $('#expiry_type');
        const validated_value = value && $expiry_type.find(`option[value=${value}]`).length ? value : 'duration';
        const is_edge = window.navigator.userAgent.indexOf('Edge') !== -1;
        $expiry_type.val(validated_value);

        let make_price_request = 0;
        if (validated_value === 'endtime') {
            Durations.displayEndTime();
            if (Defaults.get(EXPIRY_DATE)) {
                // if time changed, proposal will be sent there if not we should send it here
                make_price_request = Durations.selectEndDate(moment(Defaults.get(EXPIRY_DATE))) ? -1 : 1;
            }
            Defaults.remove(DURATION_AMOUNT, DURATION_UNITS);
            if (is_edge) {
                document.getSelection().empty(); // microsoft edge 18 automatically start selecting text when select expiry time after changing expiry type to end time
            }
        } else {
            Durations.display();
            if (Defaults.get(DURATION_UNITS)) {
                onDurationUnitChange(Defaults.get(DURATION_UNITS));
            }
            const duration_amount = Defaults.get(DURATION_AMOUNT);
            if (duration_amount && duration_amount > $('#duration_minimum').text()) {
                $('#duration_amount').val(duration_amount);
            }
            make_price_request = 1;
            Defaults.remove(END_DATE, EXPIRY_DATE, EXPIRY_TIME);
            Durations.validateMinDurationAmount();
        }
        displayEquals(validated_value);

        return make_price_request;
    };

    const onDurationUnitChange = (value) => {
        const $duration_units = $('#duration_units');
        if (!value || !$duration_units.find(`option[value=${value}]`).length) {
            return 0;
        }

        $duration_units.val(value);
        Defaults.set(DURATION_UNITS, value);

        Durations.selectUnit(value);
        Durations.populate();

        return 1;
    };

    return {
        displayEquals,
        processActiveSymbols,
        processMarket,
        processContract,
        processContractForm,
        forgetTradingStreams,
        onExpiryTypeChange,
        onDurationUnitChange,
    };
})();

module.exports = Process;
